import React, { useMemo } from 'react';
import useSWR from 'swr';
import {
  Box,
  Flex,
  Input,
  InputGroup,
  InputRightAddon,
  Stack,
  Text,
} from '@chakra-ui/react';
import { fetchData } from '../../services/fetch';

/**
 * GRI-08 — as medidas do indicador COMO O RESPONDENTE AS VÊ.
 *
 * POR QUE EXISTE, tendo `MedidasGri` ao lado
 *   Os dois mostram a mesma informação para leitores diferentes. O `MedidasGri`
 *   é a lista compacta da AUDITORIA: o avaliador quer conferir números rápido,
 *   alinhados à direita, muitos de uma vez. Este aqui é a tela de "visualizar
 *   como respondente", cujo propósito é fidelidade — se ela não for igual ao
 *   formulário de verdade, não serve para nada, porque o gestor a usa
 *   justamente para saber o que o cliente está vendo.
 *
 *   Por isso ele imita o `CamposIndicadorGri` do gs-form-survey-ui: mesma caixa
 *   verde, mesmo empilhamento, campo com a unidade no sufixo. Somente leitura,
 *   porque aqui ninguém responde.
 *
 * ACEITA `gri` (objetos prontos, telas de questionário) OU `griIds` (só os ids,
 * que é o que o FORMULÁRIO do template guarda). Sem o `griIds` esta prévia
 * simplesmente não aparecia ao montar o template — o gestor via a tag do código
 * mas não o nome/descrição/unidade do indicador, e tinha que publicar e abrir
 * como respondente pra conferir. Resolve contra o catálogo pela MESMA chave de
 * SWR do GriMultiSelect / RotuloGri desta tela (o SWR deduplica).
 *
 * DUAS CÓPIAS, DE PROPÓSITO: os frontends têm stacks incompatíveis (CRA/JS aqui,
 * Vite/TS lá) e o CLAUDE.md proíbe unificá-los. O custo é manter as duas em
 * sincronia; quem mexer no visual de um lado precisa mexer no outro.
 */
const MedidasGriComoFormulario = ({ gri, griIds }) => {
  const precisaResolver = !gri?.length && !!griIds?.length;
  const { data: catalogo } = useSWR(
    precisaResolver ? '/gri-indicator' : null,
    fetchData,
  );

  const indicadores = useMemo(() => {
    if (gri?.length) return gri;
    if (!griIds?.length) return [];
    // Enquanto o catálogo não chega, `indicadores` fica vazio e a caixa não
    // pinta — as tags de código do RotuloGri (acima, no card) já dão o retorno
    // imediato; meia caixa sem nome/unidade seria pior que a espera de 1 frame.
    return griIds
      .map((id) =>
        (catalogo || []).find((item) => item.id_gri_indicator === Number(id)),
      )
      .filter(Boolean);
  }, [gri, griIds, catalogo]);

  if (!indicadores.length) return null;

  return (
    <Box
      bg='#F7FBF8'
      border='1px solid #CDE8D6'
      borderRadius='8px'
      p='16px'
      mt='4px'
    >
      <Text fontSize='12px' fontWeight={700} color='#1B5E30' mb='2px'>
        Medidas dos indicadores GRI
      </Text>
      <Text fontSize='12px' color='#5C6066' mb='12px'>
        Cada indicador tem a sua medida. Informe apenas números — use vírgula
        para o decimal.
      </Text>

      <Stack spacing='14px'>
        {indicadores.map((indicador) => (
          <Box key={indicador.id_gri_indicator}>
            <Text fontSize='14px' fontWeight={600} color='#16262E'>
              {indicador.code}
              {indicador.name ? ` — ${indicador.name}` : ''}
            </Text>

            {indicador.description && (
              <Text
                fontSize='12px'
                color='#5C6066'
                mt='2px'
                mb='6px'
                whiteSpace='pre-wrap'
              >
                {indicador.description}
              </Text>
            )}

            <Flex maxW='320px' mt={indicador.description ? '0' : '6px'}>
              <InputGroup size='md'>
                {/* Sem valor de propósito: o preview mostra o formulário EM
                    BRANCO, como o respondente o encontra. Quem quer conferir o
                    que a empresa respondeu usa a tela de auditoria. */}
                <Input
                  placeholder='Ex.: 1234,56'
                  isReadOnly
                  bg='#FFF'
                  borderRadius='8px'
                  fontSize='sm'
                  fontWeight={600}
                />
                {indicador.unit && (
                  <InputRightAddon
                    bg='#EAF7EE'
                    color='#1B5E30'
                    fontSize='12px'
                    fontWeight={700}
                    borderRadius='8px'
                  >
                    {indicador.unit}
                  </InputRightAddon>
                )}
              </InputGroup>
            </Flex>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

export default MedidasGriComoFormulario;
